<template>
  <div class="science-page">
    <header-bar></header-bar>

    <!-- 1. 页头横幅 -->
    <section class="banner">
      <img
        class="banner-bg"
        :src="require('@/assets/banner_neural.jpg')"
        alt="般意 PanBrain 品牌蓝色神经网络球视觉背景"
      />
      <div class="banner-mask"></div>
      <div class="banner-inner">
        <h1 class="banner-title">科学与研究</h1>
        <p class="banner-subtitle">硬核不是形容词，是每一个都可以查证的事实</p>
      </div>
    </section>

    <!-- 2. 创始团队论文 -->
    <section class="section section--white">
      <div class="container">
        <h2 class="section-title">创始团队学术论文（{{ founder.sciPapers }} 篇）</h2>
        <p class="section-desc">
          创始人{{ founder.name }}博士（{{ founder.education[0] }}）团队累计发表
          {{ founder.sciPapers }} 篇学术论文（含 SCI 期刊论文与国际会议论文），附期刊与影响因子信息，全部公开可查证。
        </p>
        <ul class="paper-list">
          <li class="paper-item" v-for="(paper, index) in papers" :key="index">
            <div class="paper-year">{{ paper.year }}</div>
            <div class="paper-main">
              <div class="paper-title">{{ paper.title }}</div>
              <div class="paper-journal">{{ paper.journal }}</div>
            </div>
            <a
              v-if="paper.pubmedUrl"
              class="paper-link"
              :href="paper.pubmedUrl"
              target="_blank"
              rel="noopener noreferrer"
              >查看论文 →</a
            >
          </li>
        </ul>
      </div>
    </section>

    <!-- 3. 知识产权（专利清单） -->
    <section class="section section--gray">
      <div class="container">
        <h2 class="section-title">知识产权（已授权专利 {{ patents.length }} 项）</h2>
        <p class="section-desc">
          发明专利 {{ patentStats.invention }} 项 · 实用新型
          {{ patentStats.utility }} 项 · 外观设计
          {{ patentStats.design }} 项，专利号全部公示，可在国家知识产权局查询。
        </p>
        <ul class="patent-list">
          <li
            class="patent-item"
            v-for="(patent, index) in patents"
            :key="index"
          >
            <span class="patent-type" :class="'patent-type--' + index % 3">{{
              patent.type
            }}</span>
            <span class="patent-name">{{ patent.name }}</span>
            <a
              class="patent-no"
              :href="patent.queryUrl"
              target="_blank"
              rel="noopener noreferrer"
              >{{ patent.patentNo }}</a
            >
          </li>
        </ul>
      </div>
    </section>

    <!-- 4. 安全性说明 -->
    <section class="section section--white">
      <div class="container">
        <h2 class="section-title">安全性说明</h2>
        <div class="safety-grid">
          <div class="safety-card">
            <div class="safety-card-title">60 年研究历史</div>
            <div class="safety-card-text">{{ safety.tdcsHistory }}</div>
          </div>
          <div class="safety-card">
            <div class="safety-card-title">微弱电流强度</div>
            <div class="safety-card-text">{{ safety.intensity }}</div>
          </div>
          <div class="safety-card">
            <div class="safety-card-title">常见可逆反应</div>
            <div class="safety-card-text">{{ safety.sideEffects }}</div>
          </div>
        </div>
        <div class="contra">
          <h3 class="contra-title">以下人群请勿使用</h3>
          <ul class="contra-list">
            <li
              class="contra-item"
              v-for="(item, index) in safety.contraindications"
              :key="index"
            >
              {{ item }}
            </li>
          </ul>
        </div>
        <p class="safety-advice">{{ safety.advice }}</p>
      </div>
    </section>

    <!-- 5. 检测与认证 -->
    <section class="section section--gray">
      <div class="container">
        <h2 class="section-title">
          {{ keyFacts.certifications }} 项检测认证，逐项说人话
        </h2>
        <p class="section-note">证书编号陆续公示中，欢迎通过官方渠道核验。</p>
        <img
          class="cert-wall"
          src="../../assets/renzheng_logo.png"
          alt="般意精力舱八项国内外检测认证标志墙"
        />
        <div class="cert-grid">
          <div class="cert-card" v-for="(cert, index) in certifications" :key="index">
            <div class="cert-head">
              <span class="cert-name">{{ cert.name }}</span>
              <span v-if="cert.year" class="cert-year">{{ cert.year }} 年</span>
            </div>
            <div class="cert-plain">{{ cert.plain }}</div>
            <div v-if="cert.certNo" class="cert-no">
              证书编号：{{ cert.certNo }}
            </div>
            <a
              v-if="cert.queryUrl"
              class="cert-link"
              :href="cert.queryUrl"
              target="_blank"
              rel="noopener noreferrer"
              >查询入口 →</a
            >
          </div>
        </div>
      </div>
    </section>

    <!-- 6. 数据与算法 -->
    <section class="section section--white">
      <div class="container">
        <h2 class="section-title">数据与算法</h2>
        <div class="tech-grid">
          <div class="tech-card">
            <div class="tech-card-title">EEG 数据库</div>
            <div class="tech-card-text">{{ tech.eegDatabase }}</div>
          </div>
          <div class="tech-card">
            <div class="tech-card-title">算法迭代</div>
            <div class="tech-card-text">{{ tech.algorithmHistory }}</div>
          </div>
        </div>
      </div>
    </section>

    <footer-info></footer-info>
  </div>
</template>

<script>
import headerBar from "@/components/common/HeaderBar";
import footerInfo from "@/components/common/FooterInfo";
import {
  founder,
  papers,
  patents,
  patentStats,
  safety,
  tech,
  certifications,
  keyFacts,
} from "@/data/facts";

export default {
  name: "Science",
  data() {
    return {
      founder,
      papers,
      patents,
      patentStats,
      safety,
      tech,
      certifications,
      keyFacts,
    };
  },
  components: {
    headerBar,
    footerInfo,
  },
};
</script>

<style scoped>
.science-page {
  width: 100vw;
  background: #ffffff;
  color: #333333;
  font-family: "SourceHanSansCN-Regular", sans-serif;
}

.container {
  width: 73.4vw;
  margin: 0 auto;
}

/* ---------- 1. 页头横幅 ---------- */
.banner {
  position: relative;
  width: 100vw;
  height: 26vw;
  overflow: hidden;
}

.banner-bg {
  display: block;
  width: 100vw;
  height: 26vw;
  object-fit: cover;
}

.banner-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(11, 13, 68, 0.55) 0%,
    rgba(27, 30, 138, 0.35) 55%,
    rgba(11, 13, 68, 0.6) 100%
  );
}

.banner-inner {
  position: absolute;
  top: 7vw;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.banner-title {
  font-family: "SourceHanSansCN-Bold", sans-serif;
  font-size: 2.5vw;
  color: #ffffff;
  margin: 0 0 1.4vw;
  letter-spacing: 0.35vw;
  text-shadow: 0 0.15vw 1vw rgba(11, 13, 68, 0.55);
}

.banner-subtitle {
  font-family: "SourceHanSansCN-Light", sans-serif;
  font-size: 1.15vw;
  color: #e3e5fb;
  margin: 0;
  letter-spacing: 0.12vw;
  text-shadow: 0 0.1vw 0.8vw rgba(11, 13, 68, 0.5);
}

/* ---------- 通用区块 ---------- */
.section {
  padding: 4.5vw 0;
}

.section--white {
  background: #ffffff;
}

.section--gray {
  background: #f7f8fa;
}

.section-title {
  font-family: "SourceHanSansCN-Bold", sans-serif;
  font-size: 2.5vw;
  color: #1b1e8a;
  text-align: center;
  margin: 0 0 2.5vw;
}

.section-desc {
  font-size: 1vw;
  line-height: 1.9;
  color: #333333;
  text-align: center;
  margin: -1vw auto 1vw;
  max-width: 60vw;
}

.section-note {
  font-family: "SourceHanSansCN-Normal", sans-serif;
  font-size: 0.8vw;
  color: #999999;
  text-align: center;
  margin: 0 0 2vw;
}

/* ---------- 2. 论文列表 ---------- */
.paper-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.paper-item {
  display: flex;
  align-items: center;
  padding: 1.6vw 0.4vw;
  border-bottom: 1px solid #e9ebf5;
}

.paper-item:first-child {
  border-top: 1px solid #e9ebf5;
}

.paper-year {
  flex: 0 0 10vw;
  font-family: "SourceHanSansCN-Bold", sans-serif;
  font-size: 1.9vw;
  line-height: 1.2;
  color: #1b1e8a;
}

.paper-main {
  flex: 1;
  padding-right: 1.5vw;
}

.paper-title {
  font-family: "SourceHanSansCN-Medium", sans-serif;
  font-size: 1vw;
  line-height: 1.7;
  color: #333333;
}

.paper-journal {
  font-size: 0.85vw;
  color: #888888;
  margin-top: 0.35vw;
}

.paper-link {
  flex: 0 0 auto;
  font-family: "SourceHanSansCN-Medium", sans-serif;
  font-size: 0.85vw;
  color: #1b1e8a;
  text-decoration: none;
  border: 1px solid #888ef9;
  border-radius: 1.2vw;
  padding: 0.35vw 1vw;
}

.paper-link:hover {
  background: #1b1e8a;
  border-color: #1b1e8a;
  color: #ffffff;
}

/* ---------- 3. 产品研究 ---------- */
.study-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2.8vw;
}

.study-left {
  width: 38vw;
}

.study-scale {
  font-family: "SourceHanSansCN-Bold", sans-serif;
  font-size: 1.6vw;
  color: #1b1e8a;
  margin-bottom: 1.2vw;
}

.study-summary {
  font-size: 1vw;
  line-height: 1.9;
  color: #333333;
  margin: 0 0 2.2vw;
}

.study-rate {
  background: #ffffff;
  border-radius: 0.6vw;
  box-shadow: 0 0.5vw 2vw rgba(27, 30, 138, 0.08);
  padding: 1.8vw 2.2vw;
}

.study-rate-value {
  font-family: "SourceHanSansCN-Bold", sans-serif;
  font-size: 3.2vw;
  line-height: 1.25;
  color: #1b1e8a;
}

.study-rate-label {
  font-family: "SourceHanSansCN-Medium", sans-serif;
  font-size: 0.95vw;
  color: #555555;
  margin-top: 0.4vw;
}

.study-right {
  width: 32vw;
}

.study-img {
  display: block;
  width: 32vw;
  border-radius: 1vw;
  box-shadow: 0 0.8vw 2.6vw rgba(27, 30, 138, 0.14);
}

.study-detail {
  display: flex;
  justify-content: space-between;
  margin-bottom: 2.5vw;
}

.study-detail-item {
  width: 23.5vw;
  background: #ffffff;
  border-radius: 0.5vw;
  padding: 1.6vw;
  box-sizing: border-box;
  box-shadow: 0 0.4vw 1.6vw rgba(27, 30, 138, 0.07);
}

.study-detail-label {
  font-family: "SourceHanSansCN-Medium", sans-serif;
  font-size: 1vw;
  color: #1b1e8a;
  margin-bottom: 0.7vw;
}

.study-detail-text {
  font-size: 0.9vw;
  line-height: 1.8;
  color: #333333;
}

.study-pdf {
  text-align: center;
}

.btn {
  display: inline-block;
  font-family: "SourceHanSansCN-Medium", sans-serif;
  font-size: 1vw;
  border-radius: 1.6vw;
  padding: 0.8vw 2.6vw;
  text-decoration: none;
}

.btn--primary {
  background: #1b1e8a;
  color: #ffffff;
}

.btn--primary:hover {
  background: #888ef9;
}

.btn--disabled {
  background: #d8dae2;
  color: #9a9daa;
  cursor: not-allowed;
}

/* ---------- 4. 安全性 ---------- */
.safety-grid {
  display: flex;
  justify-content: space-between;
  margin-bottom: 3vw;
}

.safety-card {
  width: 23.5vw;
  background: #ffffff;
  border-top: 0.3vw solid #888ef9;
  border-radius: 0.5vw;
  padding: 1.8vw 1.6vw;
  box-sizing: border-box;
  box-shadow: 0 0.5vw 1.8vw rgba(27, 30, 138, 0.08);
}

.safety-card-title {
  font-family: "SourceHanSansCN-Bold", sans-serif;
  font-size: 1.1vw;
  color: #1b1e8a;
  margin-bottom: 0.8vw;
}

.safety-card-text {
  font-size: 0.9vw;
  line-height: 1.8;
  color: #333333;
}

.contra {
  position: relative;
  border: 1px solid #f0c8c8;
  border-left: 0.35vw solid #e05252;
  border-radius: 0.6vw;
  background: #fdf4f4;
  padding: 2vw 2.5vw;
  margin-bottom: 2vw;
  box-shadow: 0 0.4vw 1.6vw rgba(224, 82, 82, 0.08);
}

.contra-title {
  font-family: "SourceHanSansCN-Bold", sans-serif;
  font-size: 1.3vw;
  color: #c0392b;
  text-align: center;
  margin: 0 0 1.5vw;
}

.contra-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.contra-item {
  font-family: "SourceHanSansCN-Medium", sans-serif;
  font-size: 0.95vw;
  color: #c0392b;
  background: #ffffff;
  border: 1px solid #e8a0a0;
  border-radius: 1.4vw;
  padding: 0.5vw 1.4vw;
  margin: 0.4vw;
}

.safety-advice {
  font-size: 0.9vw;
  line-height: 1.8;
  color: #888888;
  text-align: center;
  margin: 0;
}

/* ---------- 5. 检测与认证 ---------- */
.cert-wall {
  display: block;
  width: 73.4vw;
  height: 4.17vw;
  margin: 0 auto 2.8vw;
}

.cert-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.cert-card {
  width: 17.6vw;
  background: #ffffff;
  border-radius: 0.5vw;
  padding: 1.4vw;
  box-sizing: border-box;
  margin-bottom: 1.2vw;
  box-shadow: 0 0.4vw 1.6vw rgba(27, 30, 138, 0.07);
}

.cert-head {
  margin-bottom: 0.8vw;
}

.cert-name {
  font-family: "SourceHanSansCN-Bold", sans-serif;
  font-size: 1vw;
  color: #1b1e8a;
  line-height: 1.5;
}

.cert-year {
  display: inline-block;
  font-size: 0.75vw;
  color: #888ef9;
  border: 1px solid #888ef9;
  border-radius: 0.8vw;
  padding: 0.1vw 0.6vw;
  margin-left: 0.5vw;
  vertical-align: middle;
}

.cert-plain {
  font-size: 0.85vw;
  line-height: 1.8;
  color: #333333;
  margin-bottom: 0.8vw;
}

.cert-no {
  font-size: 0.8vw;
  line-height: 1.6;
  color: #888888;
  word-break: break-all;
  margin-bottom: 0.6vw;
}

.cert-link {
  font-family: "SourceHanSansCN-Medium", sans-serif;
  font-size: 0.85vw;
  color: #1b1e8a;
  text-decoration: none;
}

.cert-link:hover {
  color: #888ef9;
}

/* ---------- 6. 数据与算法 ---------- */
.tech-grid {
  display: flex;
  justify-content: space-between;
}

.tech-card {
  width: 35.6vw;
  background: #ffffff;
  border-top: 0.3vw solid #1b1e8a;
  border-radius: 0.5vw;
  padding: 2vw 2.2vw;
  box-sizing: border-box;
  box-shadow: 0 0.5vw 1.8vw rgba(27, 30, 138, 0.08);
}

.tech-card-title {
  font-family: "SourceHanSansCN-Bold", sans-serif;
  font-size: 1.15vw;
  color: #1b1e8a;
  margin-bottom: 0.8vw;
}

.tech-card-text {
  font-size: 0.95vw;
  line-height: 1.8;
  color: #333333;
}
.patent-list {
  margin: 2vw 0 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 2.5vw;
}

.patent-item {
  display: flex;
  align-items: center;
  padding: 0.9vw 0;
  border-bottom: 1px solid #e3e6f3;
  font-size: 0.85vw;
}

.patent-type {
  flex: none;
  font-family: "SourceHanSansCN-Medium", sans-serif;
  color: #888ef9;
  border: 1px solid #888ef9;
  border-radius: 1vw;
  padding: 0.1vw 0.6vw;
  font-size: 0.72vw;
  margin-right: 0.9vw;
}

.patent-name {
  flex: 1;
  color: #333333;
  font-family: "SourceHanSansCN-Regular", sans-serif;
  margin-right: 0.9vw;
}

.patent-no {
  flex: none;
  color: #1b1e8a;
  font-family: "SourceHanSansCN-Medium", sans-serif;
  white-space: nowrap;
}

.patent-no:hover {
  text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/Science.vue